// Full Estimate — service-line voice (mirrors QE3).
//
// 8 logical steps:
//   1. Vehicle    (reused from QE3 / QE2)
//   2. Photos     (reused from QE3 — quality verdict)
//   3. Tags       (reused from QE3 — AI side review + edit)
//   4. Customer   (reused from QE3 step-send in "continue" mode)
//   5. Generate   (animated processing screen)
//   6. Damage     (sections + components editor — this folder)
//   7. Operations (read-only operations review — this folder)
//   8. Review     (final review + send — this folder)

export const FE_WIZARD_COPY = {
  leaveLabel: "Leave for now",
  leaveConfirmTitle: "Leave this estimate?",
  leaveConfirmBody:
    "Your work is saved on this device until the tab closes. Open this URL again to pick up.",
  leaveConfirmContinue: "Stay here",
  leaveConfirmExit: "Leave",
  noTeamTitle: "Pick a team to start",
  noTeamBody:
    "Full estimates need a dealership or body shop selected before they can run.",
  noTeamCta: "Open team picker",
}

// Customer step (step 4) — reuses QE3's StepSend in "continue" mode.
// Eyebrow override is passed in via the wizard.
export const FE_CUSTOMER_EYEBROW = "Step 4 of 8 · Customer"
export const FE_CUSTOMER_CONTINUE_CTA = "Continue"

export const FE_PROCESS_COPY = {
  eyebrow: "Step 5 of 8 · Generate",
  title: "Connecting the dots",
  subtitle:
    "AI is analyzing your photos and assembling the line items the shop will price.",
  // Recap copy
  recapEdit: "Edit",
  recapPhotoCountOne: "1 photo",
  recapPhotoCountMany: (n: number) => `${n} photos`,
  recapSidesCovered: (n: number) =>
    n === 1 ? "1 angle" : `${n} angles`,
  // Stage labels — only the 3 stages the `damage` agent actually
  // covers. Repair-vs-replace + pricing happen on the
  // sections-orchestrator pass (after Damage editor → Next), so they
  // live in FE_ORCHESTRATOR_COPY below.
  stages: [
    {
      id: "read",
      label: "Reading damage from photos",
      tickers: [
        "Decoding pixel data…",
        "Spotting impact zones…",
        "Mapping damage geometry…",
      ],
    },
    {
      id: "sections",
      label: "Identifying affected sections",
      tickers: [
        "Locating bumper regions…",
        "Cross-referencing body shape…",
        "Outlining panel boundaries…",
      ],
    },
    {
      id: "components",
      label: "Selecting components & parts",
      tickers: [
        "Looking up OEM catalog…",
        "Comparing aftermarket equivalents…",
        "Matching trim level…",
      ],
    },
  ] as const,
  // Live status header (above stages, the "AI thinks…" line).
  thinking: "AI is working on your estimate.",
  ready: "Estimate ready.",
  // Ticker prefix.
  tickerPrefix: "Working on:",
  // CTA shown after stages complete + lineItems arrived.
  viewCta: "View your estimate",
  viewCtaSubtitle: "Line items, pricing, and the operations breakdown.",
  // Error state.
  errorTitle: "Couldn't generate the estimate",
  errorBody:
    "Something interrupted the AI run. Your photos and details are still here — try again.",
  errorRetry: "Try again",
  errorBack: "Back to tags",
  // Awaiting state — shown if backend completes before stage choreography.
  syncing: "Wrapping up the analysis…",
}

// Orchestrator loader — shown over the Damage step after Next while
// `sections-orchestrator` runs. Picks up the 2 stages that the
// damage agent didn't cover (repair-vs-replace decisions + pricing).
// Same visual primitives as FE_PROCESS_COPY (stage list, ticker,
// silhouette card optional).
export const FE_ORCHESTRATOR_COPY = {
  title: "Re-pricing the estimate",
  subtitle: "Re-deriving operations and labor based on your edits.",
  stages: [
    {
      id: "repair-replace",
      label: "Choosing repair vs replace",
      tickers: [
        "Estimating panel deformation…",
        "Weighing labor vs new part…",
        "Flagging structural concerns…",
      ],
    },
    {
      id: "price",
      label: "Pricing the operations",
      tickers: [
        "Pulling labor rates…",
        "Calculating paint refinish hours…",
        "Building cost range…",
      ],
    },
  ] as const,
  thinking: "Updating your estimate.",
  syncing: "Almost there…",
  ready: "Updated.",
  tickerPrefix: "Working on:",
  errorTitle: "Couldn't update the estimate",
  errorBody:
    "Something interrupted the orchestrator. Your edits are kept — try again.",
  errorRetry: "Try again",
  errorBack: "Back to damage",
}

export const FE_DAMAGE_COPY = {
  eyebrow: "Step 6 of 8 · Damage",
  title: "Components by section",
  subtitle:
    "Review what was identified. Edit, add, or remove components and sections.",
  loading: "Pulling in the analysis…",
  emptySection: "No components yet — add one below.",
  addComponent: "Add component",
  addSection: "Add a section",
  descriptionLabel: "Description",
  descriptionPlaceholder: "What does the damage look like?",
  actionLabel: "Action",
  partTypeLabel: "Part type",
  sideLabel: "Side",
  deleteRow: "Delete component",
  doneEditing: "Done",
  saving: "Saving…",
  continue: "Continue to operations",
  back: "Back",
}

// Read-only operations review. Operations are children of line items,
// populated by sections-orchestrator after Damage saves. No editor
// surface — the user reviews what the orchestrator decided.
export const FE_OPERATIONS_COPY = {
  eyebrow: "Step 7 of 8 · Operations",
  title: "Labor operations",
  subtitle:
    "Review the labor tasks the analysis assigned. Edit components on the previous step to change these.",
  readOnlyHint:
    "Operations are derived from your component edits. Go back to Damage to make changes.",
  loading: "Loading operations…",
  empty: "No operations yet.",
  continue: "Continue to review",
  back: "Back to damage",
}

export const FE_REVIEW_COPY = {
  eyebrow: "Step 8 of 8 · Review",
  title: "Final estimate",
  subtitle: "Confirm everything before sending.",
  loading: "Pricing the estimate…",
  vehicleLabel: "Vehicle",
  customerLabel: "Customer",
  partsLabel: "Parts",
  operationsLabel: "Operations",
  totalEstimate: "Total estimate",
  professionalView: "Professional",
  customerView: "Customer",
  saveDraft: "Save draft",
  sendEstimate: "Send estimate",
  backToOperations: "Back to operations",
  successTitle: "Estimate sent",
  successBody: "The shop will see this in their queue.",
  successPrimary: "View estimate",
  successSecondary: "Start another",
  noLineItems: "No components",
  noOperations: "No operations",
  hours: (h: number) => `${h.toFixed(1)} hr`,
}
